import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Zap, ShieldCheck, ArrowRight, Loader2, QrCode } from "lucide-react";
import { api, fileUrl, apiError, timeAgo, money } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { StatusPill, LiveIndicator } from "@/components/Pills";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function CustomerPage() {
  const [history, setHistory] = useState([]);
  const [sessionIds, setSessionIds] = useState([]);

  const loadHistory = () => api.get("/public/submissions").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    loadHistory();
    const t = setInterval(loadHistory, 4000);
    return () => clearInterval(t);
  }, []);

  const onSubmitted = (sub) => {
    if (sub?.id) setSessionIds((p) => (p.includes(sub.id) ? p : [...p, sub.id]));
    loadHistory();
  };

  const visible = history.filter((h) => sessionIds.includes(h.id));

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/branch/login"
            data-testid="nav-branch-dashboard-link"
            className="text-sm font-medium text-slate-500 hover:text-emerald-600 inline-flex items-center gap-1 transition-colors"
          >
            Branch <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-10">
        <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold mb-5">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure UPI Payments
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Pay in seconds.
        </h1>
        <p className="mt-4 text-slate-500 text-base leading-relaxed">
          Enter your amount, scan the QR we show you, then upload your screenshot — we verify it automatically.
        </p>

        <PayCta onSubmitted={onSubmitted} />

        <section className="mt-12">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold tracking-tight">Payment History</h2>
              <p className="text-sm text-slate-500">Shows payments made in this session — status updates automatically.</p>
            </div>
            <LiveIndicator />
          </div>

          <div className="mt-5 space-y-3">
            {visible.length === 0 && (
              <p className="text-sm text-slate-400 py-8 text-center">No payments yet in this session. Make one above to track it live.</p>
            )}
            {visible.map((h) => (
              <div
                key={h.id}
                data-testid="history-item"
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white shadow-[0_2px_12px_-6px_rgba(16,185,129,0.15)]"
              >
                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                  {h.screenshot_url ? (
                    <img src={fileUrl(h.screenshot_url)} alt="proof" className="w-full h-full object-cover" />
                  ) : (
                    <QrCode className="w-5 h-5 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-slate-500 truncate">{h.transaction_id}</div>
                  <div className="text-sm font-semibold truncate">{h.business_name}</div>
                  <div className="text-xs text-slate-400">{timeAgo(h.submitted_at)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold">{money(h.amount)}</div>
                  <div className="mt-1"><StatusPill status={h.status} /></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function PayCta({ onSubmitted }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 p-6 rounded-2xl border border-slate-200 bg-white shadow-[0_4px_28px_-6px_rgba(16,185,129,0.12)]">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-200 flex items-center justify-center shrink-0">
          <QrCode className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <div className="font-bold text-lg tracking-tight">Pay via UPI</div>
          <p className="text-sm text-slate-500 mt-0.5">
            Enter your amount and we'll show the right QR to scan.
          </p>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            data-testid="pay-via-upi-button"
            className="w-full mt-5 h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base rounded-xl"
          >
            <Zap className="w-4 h-4 mr-1" /> Pay via UPI
          </Button>
        </DialogTrigger>
        {open && <PaymentWizard onDone={(sub) => { setOpen(false); onSubmitted(sub); }} />}
      </Dialog>
    </div>
  );
}

function PaymentWizard({ onDone }) {
  const [step, setStep] = useState("amount");
  const [amount, setAmount] = useState("");
  const [qr, setQr] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  const [txn, setTxn] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [payer, setPayer] = useState({});
  const [ocrBusy, setOcrBusy] = useState(false);
  const [autoTxn, setAutoTxn] = useState(false);
  const [autoAmt, setAutoAmt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const continueToQr = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setLoadingQr(true);
    try {
      const { data } = await api.post("/public/qr-for-amount", { amount: amt });
      setQr(data);
      setPayAmount(String(amt));
      setStep("pay");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoadingQr(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/public/upload-and-ocr", fd);
      setScreenshotUrl(data.screenshot_url);
      setPayer({ payer_upi: data.payer_upi || null, payer_name: data.payer_name || null, utr: data.utr || null });
      if (data.transaction_id) { setTxn(data.transaction_id); setAutoTxn(true); }
      if (data.amount != null) { setPayAmount(String(data.amount)); setAutoAmt(true); }
      if (data.transaction_id || data.amount != null) toast.success("Auto-detected from screenshot");
      else toast.message("Couldn't auto-read — please type the details");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setOcrBusy(false);
    }
  };

  const submit = async () => {
    if (!txn || !payAmount) return toast.error("Transaction ID and Amount are required");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/submissions", {
        business_id: qr.id,
        transaction_id: txn.trim(),
        amount: parseFloat(payAmount),
        screenshot_url: screenshotUrl,
        ...payer,
      });
      toast.success("Payment submitted — tracking status live");
      onDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md bg-white">
      <DialogHeader>
        <DialogTitle className="tracking-tight">{step === "amount" ? "Enter amount" : "Scan & Pay"}</DialogTitle>
      </DialogHeader>

      {step === "amount" ? (
        <div className="py-2 space-y-4">
          <div>
            <Label className="text-xs">Amount (₹)</Label>
            <Input
              data-testid="pay-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && continueToQr()}
              placeholder="e.g. 500"
              inputMode="decimal"
              autoFocus
              className="mt-1 text-lg"
            />
            <p className="text-xs text-slate-400 mt-1">We'll show the right QR for this amount.</p>
          </div>
          <Button
            data-testid="continue-to-qr-button"
            onClick={continueToQr}
            disabled={loadingQr}
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl"
          >
            {loadingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center py-2">
            <img src={fileUrl(qr.qr_image)} data-testid="qr-code-image" alt="QR" className="w-40 h-40 rounded-xl border border-slate-200 p-1" />
            <div className="mt-2 text-sm font-semibold">{qr.name}</div>
            <span className="mt-1 text-xs font-mono bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-slate-600">
              {qr.upi_id}
            </span>
            <p className="mt-1 text-xs text-slate-400">Pay {money(payAmount)} · scan with any UPI app</p>
            {qr.upi_intent_url && (
              <div className="md:hidden w-full grid grid-cols-2 gap-2 mt-3" data-testid="upi-intent-buttons">
                {[["gpay_intent_url", "Google Pay"], ["phonepe_intent_url", "PhonePe"], ["paytm_intent_url", "Paytm"], ["upi_intent_url", "Other UPI App"]].map(([k, label]) => (
                  <a key={k} href={qr[k]} data-testid={`upi-intent-${k.replace("_intent_url", "")}`}
                    className="h-10 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold flex items-center justify-center hover:bg-emerald-100 transition-colors">{label}</a>
                ))}
                <p className="col-span-2 text-[11px] text-slate-400 text-center">After paying, come back here and upload your screenshot below.</p>
              </div>
            )}
            <button
              type="button"
              data-testid="change-amount-button"
              onClick={() => setStep("amount")}
              className="mt-2 text-xs text-emerald-600 hover:underline"
            >
              ← Change amount
            </button>
          </div>

          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="screenshot-file-input" />
            <button
              type="button"
              data-testid="screenshot-upload-dropzone"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-300 rounded-xl p-5 flex flex-col items-center gap-2 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
            >
              {preview ? (
                <img src={preview} alt="preview" className="h-24 rounded-lg object-contain" />
              ) : (
                <>
                  {ocrBusy ? <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" /> : <UploadCloud className="w-6 h-6 text-emerald-500" />}
                  <span className="text-sm font-medium text-slate-600">Tap to upload screenshot</span>
                  <span className="text-xs text-slate-400">Enables auto-fill + proof</span>
                </>
              )}
              {preview && ocrBusy && <span className="text-xs text-emerald-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Reading…</span>}
            </button>
          </div>

          <div className="space-y-3 mt-1">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Transaction ID</Label>
                {autoTxn && <span className="text-[11px] text-emerald-600 font-medium">Auto-detected ✓</span>}
              </div>
              <Input
                data-testid="ocr-extracted-utr-input"
                value={txn}
                onChange={(e) => { setTxn(e.target.value); setAutoTxn(false); }}
                placeholder="e.g. TX123456"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Amount (₹)</Label>
                {autoAmt && <span className="text-[11px] text-emerald-600 font-medium">Auto-detected ✓</span>}
              </div>
              <Input
                data-testid="ocr-extracted-amount-input"
                value={payAmount}
                onChange={(e) => { setPayAmount(e.target.value); setAutoAmt(false); }}
                placeholder="e.g. 500"
                inputMode="decimal"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            data-testid="submit-payment-proof-button"
            onClick={submit}
            disabled={submitting || ocrBusy}
            className="w-full h-11 mt-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Payment"}
          </Button>
        </>
      )}
    </DialogContent>
  );
}
